import { useEffect, useMemo, useRef, useState } from 'react';
import { FontIcon } from '@fluentui/react';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  FluentProvider,
  MessageBar,
  MessageBarBody,
  OverlayDrawer,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useBoolean } from '@fluentui/react-hooks';
import { useSetAtom } from 'jotai';
import {
  FilterType,
  PagedDataGrid,
  useActiveTheme,
  useFetch,
  useIsMobile,
  type DataGridColumn,
  type ICommandBarItem,
  type IFilterableColumn,
  type IPagedDataGridRefType,
} from '@moko/ui';
import ConfirmDialog from '../../common/ConfirmDialog';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  deleteTenantPricingRate,
  getPricingItems,
  getTenantPricingRates,
  type IPricingItem,
  type ITenantPricingRate,
} from '../../services/pricingService';
import {
  getCurrencies,
  type ICatalogCurrency,
} from '../../services/tenantService';
import { formatDateOnlyForDisplay } from '../../utils/dateOnly';
import TenantPricingOverrideForm from './TenantPricingOverrideForm';
import {
  getOverrideErrorMessage,
  getOverrideStatus,
  pricingItemLabel,
} from './tenantPricingOverrideHelpers';

type DrawerState = { mode: 'create' } | { mode: 'edit'; rateId: number };

export interface TenantPricingOverridesProps {
  tenantId: number;
  isAdmin: boolean;
}

// Mount with key={tenantId} so rows, paging, forms and requests reset per tenant.
const TenantPricingOverrides = ({
  tenantId,
  isAdmin,
}: TenantPricingOverridesProps) => {
  const theme = useActiveTheme();
  const isMobile = useIsMobile();
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const gridRef = useRef<IPagedDataGridRefType>(null);
  const [selected, setSelected] = useState<ITenantPricingRate>();
  const [drawer, setDrawer] = useState<DrawerState>();
  const [listError, setListError] = useState<string>();
  const [isDeleteOpen, { setTrue: showDelete, setFalse: hideDelete }] =
    useBoolean(false);
  const deleteAbort = useRef<AbortController | null>(null);

  useEffect(() => () => deleteAbort.current?.abort(), []);

  const itemsFetch = useFetch<IPricingItem[]>({
    queryKey: ['pricingItems'],
    queryFn: async (abortController) =>
      [...(await getPricingItems(abortController, 500, 1)).items].sort(
        (a, b) => a.sortOrder - b.sortOrder
      ),
  });
  const currenciesFetch = useFetch<ICatalogCurrency[]>({
    queryKey: ['currencies'],
    queryFn: getCurrencies,
  });
  const pricingItems = useMemo(() => itemsFetch.data ?? [], [itemsFetch.data]);
  const currencies = currenciesFetch.data ?? [];
  const itemLabels = useMemo(
    () =>
      new Map(pricingItems.map((item) => [item.id, pricingItemLabel(item)])),
    [pricingItems]
  );
  const catalogError = itemsFetch.error ?? currenciesFetch.error;

  const columns: DataGridColumn<ITenantPricingRate>[] = [
    {
      key: 'pricing_item_id',
      name: 'Pricing item',
      minWidth: 160,
      width: 260,
      isSortable: true,
      value: (rate) =>
        itemLabels.get(rate.pricingItemId) ?? String(rate.pricingItemId),
    },
    {
      key: 'currency_code',
      name: 'Currency',
      minWidth: 80,
      width: 100,
      isSortable: true,
      value: (rate) => rate.currencyCode,
    },
    {
      key: 'amount',
      name: 'Amount',
      minWidth: 80,
      width: 120,
      isSortable: true,
      value: (rate) => Number(rate.amount).toFixed(2),
    },
    {
      key: 'effective_from',
      name: 'Effective from',
      minWidth: 110,
      width: 150,
      isSortable: true,
      value: (rate) => formatDateOnlyForDisplay(rate.effectiveFrom),
    },
    {
      key: 'effective_to',
      name: 'Effective to',
      minWidth: 110,
      width: 150,
      isSortable: true,
      value: (rate) =>
        formatDateOnlyForDisplay(rate.effectiveTo) || 'No end date',
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 80,
      width: 110,
      value: (rate) => getOverrideStatus(rate.effectiveFrom, rate.effectiveTo),
    },
  ];

  const filterColumns: IFilterableColumn[] = useMemo(
    () => [
      { key: 'id', label: 'Id', type: FilterType.Number },
      {
        key: 'pricing_item_id',
        label: 'Pricing item',
        type: FilterType.Number,
        options: pricingItems.map((item) => ({
          value: String(item.id),
          label: pricingItemLabel(item),
        })),
      },
      { key: 'currency_code', label: 'Currency', type: FilterType.String },
      { key: 'amount', label: 'Amount', type: FilterType.Number },
      { key: 'effective_from', label: 'Effective from', type: FilterType.Date },
      { key: 'effective_to', label: 'Effective to', type: FilterType.Date },
      { key: 'createdAt', label: 'Created at', type: FilterType.Date },
      { key: 'modifiedAt', label: 'Modified at', type: FilterType.Date },
    ],
    [pricingItems]
  );

  const catalogReady = !!itemsFetch.data && !!currenciesFetch.data;

  const commands: ICommandBarItem[] = [
    {
      key: 'refresh',
      text: 'Refresh',
      icon: <FontIcon iconName="Refresh" />,
      onClick: (ctx) => ctx.refresh(),
    },
    {
      key: 'newOverride',
      text: 'New',
      icon: <FontIcon iconName="Add" />,
      onClick: () => setDrawer({ mode: 'create' }),
      disabled: !isAdmin || !catalogReady,
    },
    {
      key: 'editOverride',
      text: 'Edit',
      icon: <FontIcon iconName="Edit" />,
      onClick: () => {
        if (selected) setDrawer({ mode: 'edit', rateId: selected.id });
      },
      disabled: !isAdmin || !catalogReady || !selected,
    },
    {
      key: 'deleteOverride',
      text: 'Delete',
      icon: <FontIcon iconName="Delete" />,
      onClick: () => showDelete(),
      disabled: !isAdmin || !selected,
    },
  ];

  const closeDrawer = () => setDrawer(undefined);

  return (
    <>
      {catalogError && (
        <MessageBar intent="error">
          <MessageBarBody>
            {getOverrideErrorMessage(
              catalogError,
              'Failed to load pricing items or currencies.'
            )}
          </MessageBarBody>
        </MessageBar>
      )}
      {listError && (
        <MessageBar intent="error">
          <MessageBarBody>{listError}</MessageBarBody>
        </MessageBar>
      )}
      <PagedDataGrid<ITenantPricingRate>
        ref={gridRef}
        columns={columns}
        getAction={getTenantPricingRates}
        commandBarItems={commands}
        selectionMode="single"
        selectedId={selected?.id ?? null}
        searchable={false}
        filterEnabled
        filterColumns={filterColumns}
        fillContainer
        pageSize={20}
        onSelectionChange={setSelected}
        onRowDoubleClicked={(rate) => {
          if (isAdmin && catalogReady)
            setDrawer({ mode: 'edit', rateId: rate.id });
        }}
        onLoading={() => setListError(undefined)}
        onError={(error) =>
          setListError(
            getOverrideErrorMessage(error, 'Failed to load pricing overrides.')
          )
        }
        empty={{
          title: 'No pricing overrides',
          subtitle: 'Orders for this tenant use the global pricing rates.',
        }}
      />
      <FluentProvider theme={theme}>
        <OverlayDrawer
          open={!!drawer}
          onOpenChange={(_, data) => {
            if (!data.open) closeDrawer();
          }}
          position="end"
          size={isMobile ? 'full' : 'medium'}
        >
          <DrawerHeader>
            <DrawerHeaderTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={closeDrawer}
                />
              }
            >
              {drawer?.mode === 'edit'
                ? 'Edit pricing override'
                : 'New pricing override'}
            </DrawerHeaderTitle>
          </DrawerHeader>
          <DrawerBody>
            {drawer && (
              <TenantPricingOverrideForm
                tenantId={tenantId}
                rateId={drawer.mode === 'edit' ? drawer.rateId : undefined}
                pricingItems={pricingItems}
                currencies={currencies}
                onClose={closeDrawer}
                onSaved={(message) => {
                  closeDrawer();
                  setSuccessMessage(message);
                  gridRef.current?.refresh();
                }}
              />
            )}
          </DrawerBody>
        </OverlayDrawer>
      </FluentProvider>
      <ConfirmDialog
        isModalOpen={isDeleteOpen}
        hideModal={hideDelete}
        message={`Pricing override for ${
          selected
            ? itemLabels.get(selected.pricingItemId) ?? selected.pricingItemId
            : ''
        } ${selected?.currencyCode ?? ''} would be deleted, are you sure?`}
        onYesClick={() => {
          hideDelete();
          if (!selected) return;
          deleteAbort.current?.abort();
          const abortController = new AbortController();
          deleteAbort.current = abortController;
          deleteTenantPricingRate(abortController, selected.id)
            .then(() => {
              if (abortController.signal.aborted) return;
              setSuccessMessage(`Pricing override ${selected.id} deleted.`);
              setSelected(undefined);
              gridRef.current?.refresh();
            })
            .catch((error: unknown) => {
              if (abortController.signal.aborted) return;
              setErrorMessage(
                getOverrideErrorMessage(
                  error,
                  'Failed to delete pricing override.'
                )
              );
            });
        }}
      />
    </>
  );
};

export default TenantPricingOverrides;
